import qs from 'qs';
import {
  DateRange,
  TableParams,
  Channel,
  Modify,
  JobType,
  JobSuccessEv,
  JobFailedEv,
} from 'Common/types';
import { MessageDescriptor } from 'react-intl';
import { TablePaginationConfig } from 'antd/lib/table/interface';
import { SorterResult, TableCurrentDataSource } from 'antd/es/table/interface';
import { Dayjs } from 'dayjs';

export enum ReportType {
  Hourly = 'hourly',
  Daily = 'daily',
  Weekly = 'weekly',
  Monthly = 'monthly',
  Yearly = 'yearly',
  Range = 'range',
}

export interface ReportFilterOptions {
  dateRange: DateRange;
  users?: number[];
  channel?: Channel;
  type?: ReportType;
  sort?: string;
}

export interface ProductReport {
  user: {
    id: number;
  };
  product: {
    id: number;
    name: string;
  };
  amount_sold: number;
  revenue: string;
  returned_amount: string;
  returned: number;
  discount: {
    amount: string;
    percent: string;
  };
  baskets: number;
}

export interface InvoiceReport {
  user: {
    id: number;
  };
  checkouts: number;
  walk_in_ratio: string;
  average_value: string;
  total_value: string;
}

export type ProductReportRecord = Omit<
  ProductReport,
  'product' | 'user' | 'revenue' | 'returned_amount' | 'discount'
> & {
  id: number;
  name: string | null;
  revenue: number;
  returned_amount: number;
  discount: {
    amount: number;
    percent: number;
  };
};

export type InvoiceReportRecord = Omit<
  InvoiceReport,
  'user' | 'average_value' | 'total_value'
> & {
  id: number;
  name: string;
  average_value: number;
  total_value: number;
};

export interface ProductSoldReportRecord {
  date: string;
  is_active: boolean;
  product_category: string;
  product_gtin: string;
  product_id: number;
  product_name: string;
  returned_average_item_price: string;
  returned_items: number;
  returned_revenue: string;
  returned_total_discount: string;
  revenue_overall: string;
  sold_average_item_price: string;
  sold_items: number;
  sold_overall: number;
  sold_revenue: string;
  sold_total_discount: string;
}

export interface IncomeTransactionsReportRecord {
  id: number;
  date: string;
  invoice_number: string;
  income: string;
  payment_type: string;
  employee: string;
  customer: string;
}

export interface LastTransactionsRecord extends IncomeTransactionsReportRecord {
  payment_method: MessageDescriptor;
}

export type LastTransactionsTableEntry = Omit<
  LastTransactionsRecord,
  'payment_type'
> & {
  employee: string;
  customer: string;
};

export type LastTransactionsTableInputData = {
  transactions: IncomeTransactionsReportRecord[];
};

export type RawSalesReport = {
  id: number;
  name: string;
  barcode: string;
  categories: Array<{ id: number; name: string }>;
  discount_gross: string | null;
  external_id: number | null;
  type: string | null;
  gross_price: string;
  product_code: string | null;
  sold: string | null;
  source: string | null;
  status: string;
  returned: string | number;
  revenue_gross: string | null;
  revenue_net: string | null;
};

export type SalesReport = Modify<
  RawSalesReport,
  {
    gross_price: number;
    sold: number;
    returned: number;
    total: number;
    revenue_gross: number;
    revenue_net: number;
  }
>;

export type SalesReportRecord = SalesReport & {
  position: number;
};

export type RawGiftcardRedemption = {
  completed_at: string;
  invoice_number: string;
  order_id: number;
  redeemed: string;
  remaining_value: string;
};

export type GiftcardRedemption = Modify<
  RawGiftcardRedemption,
  {
    redeemed: number;
    remaining_value: number;
  }
>;

export type RawGiftcardReport = {
  amount: string;
  created_at: string;
  current_value: string;
  id: number;
  invoice_number: string;
  name: string;
  order_id: number;
  product_id: number;
  redeem_code: string;
  redemption: Array<RawGiftcardRedemption>;
  returned_at: null | string;
  tax_rate: string;
  tax_type: number;
  type: string;
  user_id: number | null;
  valid_until: string | null;
};

export type GiftcardReport = Modify<
  RawGiftcardReport,
  {
    current_value: number;
    amount: number;
    tax_rate: number;
    redemption: Array<GiftcardRedemption>;
  }
>;

export type TipsSummaryReport = {
  key?: number;
  start_date: string;
  end_date: string;
  tax_free_tip: string | null;
  taxed_tip: string | null;
  tax_amount: string | null;
};

export type TipPerTransactionReport = {
  id: number;
  time_added: string;
  is_returned: boolean;
  invoice_number: string;
  tax_amount: number;
  taxable: boolean;
  user: number;
  user_name: string;
  amount: number;
  tax_rate: string | null;
};

export interface OrdersReportRecordPayment {
  payment_method: string;
  payment_amount: string;
}

export interface OrdersReportRecord {
  id: number;
  total: number;
  payments: OrdersReportRecordPayment[];
  [index: string]: any; // big type. Only typed the essential
}

export interface OrdersResponse {
  data: OrdersReportRecord[];
  total: number;
}

export interface ReturnsReportRecord {
  id: number;
  [index: string]: any; // big type. Only typed the essential
}

export interface ReturnsResponse {
  total: number;
  data: ReturnsReportRecord[];
}

export interface BasicReportRecord {
  date: string;
  profit: string;
  revenue: string;
  basket_count: string;
  type: ReportType;
}

export interface RevenueAndProfitChartData {
  date: string;
  originalDate: string;
  profit: number;
  revenue: number;
  type: ReportType;
}

export type HandleTableChange<T> = (
  pagination: TablePaginationConfig | boolean,
  filters: Record<string, string>,
  sorter: SorterResult<T> | SorterResult<T>[],
  extra: TableCurrentDataSource<T>
) => void;

export enum BatchReportStatus {
  Created = 'created',
  Processing = 'in_progress',
  Deleted = 'deleted',
  Finished = 'finished',
  Failed = 'failed',
}

export enum BatchReportType {
  InvoicesReturns = 'invoice_returns',
  ZReports = 'zreports',
  XLSReports = 'xls_report',
  ProductHistory = 'product_history',
  PreAccounting = 'pre_accounting',
}

// typed according /api/reports/batch_reports/ v12
export interface BatchReportRecord {
  month: string;
  month_number: number;
  year: string;
  reports: Array<BatchReportRawDetails>;
}

export interface BatchReportRawDetails {
  id: number;
  rid?: number;
  specific_timespan?: boolean;
  account?: number;
  details?: unknown | null;
  email?: string | null;
  start_date?: string;
  end_date?: string;
  filename?: string;
  year?: string;
  zip_file: string | null;
  month?: string;
  month_number?: number;
  number_of_files: number;
  batch_type: string;
  status: string;
  // status: BatchReportStatus;
}

export type BatchReportDetails = Pick<
  BatchReportRawDetails,
  'id' | 'zip_file' | 'number_of_files'
> & {
  status: BatchReportStatus;
  type: BatchReportType;
};

export type MonthlyReportsTableRecord = {
  year: number;
  month: number;
  reports: Array<BatchReportDetails>;
};

export interface SalesReportTableParams
  extends Omit<TableParams<SalesReportRecord>, 'pagination'> {
  pagination: TablePaginationConfig | boolean;
}

export type SalesReportsTableRecord = {
  data: Array<RawSalesReport>;
  total: number;
};
export type InventoryRecord = {
  product_id: number;
  product_name: string;
  date: string;
  reason: string;
  quantity: string;
  price: null;
  purchase_price: string;
  change_source: string | null;
  change_event: string | null;
  change_type: string | null;
  type: string;
  user: string;
  categories: string[];
};

export interface GiftcardReportTableParams
  extends Omit<TableParams<GiftcardReport>, 'pagination'> {
  pagination: TablePaginationConfig | boolean;
}

export interface TipsSummaryReportParams
  extends TableParams<TipsSummaryReport> {}

export type Cursor =
  | string
  | qs.ParsedQs
  | string[]
  | qs.ParsedQs[]
  | undefined
  | null;
export interface TipPerTransactionReportParams
  extends Omit<TableParams<TipPerTransactionReport>, 'pagination'> {
  cursor: Cursor;
}

export interface TipPerTransactionReportTableResult {
  next: string | null;
  previous: string | null;
  results: TipPerTransactionReport[];
}

export interface TipsCSVReportParams {
  accountId: number | undefined;
  group_by?: string;
  user_id?: number[] | undefined;
  created_at_start: Dayjs;
  created_at_end: Dayjs;
  report_type: TipsCSVReportType;
}

export enum TipsCSVReportType {
  List = 'list',
  Summary = 'summary',
}

export interface GiftcardReportTableResult {
  next: string | null;
  previous: string | null;
  count: number;
  results: GiftcardReport[];
}

export interface InventoryReportTableParams
  extends Omit<TableParams<InventoryRecord>, 'pagination'> {
  pagination: TablePaginationConfig | boolean;
}

export interface InventoryReportTableResult {
  total: number;
  data: InventoryRecord[];
}

export function isBatchReportJobSuccess(
  j: JobSuccessEv
): j is BatchReportJobEvSuccess {
  return j.type === JobType.BatchZipReport;
}

export function isBatchReportJobFailed(
  j: JobFailedEv
): j is BatchReportJobEvFailed {
  return j.type === JobType.BatchZipReport;
}

export type BatchReportJobArgs = {
  batch_type: BatchReportType;
  month_number: number;
  year: string;
};
export type BatchReportJobEvFailed = JobFailedEv<
  BatchReportJobArgs,
  { resource_id: number; data: null; error: unknown }
>;
export type BatchReportJobEvSuccess = JobSuccessEv<
  BatchReportJobArgs,
  { data: { file_url: string }; error: null; resource_id: number }
>;

export type MailReportPayload = {
  email: string;
  end: string;
  format: string;
  start: string;
  type: string;
};

export type ExportReport = MailReportPayload & { reportType: string | null };

export interface PusherBatchReport {
  account: number;
  name: string;
  fields: Fields;
  ts: Date;
  user: number;
  scope: string;
  rid: number;
  _class: string;
}

export interface PusherJobReport extends PusherBatchReport {
  source: string;
}

export interface Fields {
  status: string;
  arguments: Arguments;
  type: string;
  id: number;
  result: Result;
}

export interface Arguments {
  action: string;
  month_number: number;
  batch_type: string;
  year: string;
}

export interface Result {
  resource_id: number;
  data: Data;
  error: null;
}

export interface Data {
  file_url: string;
}

export interface DsFinvkExport {
  id: number;
  status: DsFinvkReportStatus;
  properties: {
    from_date: string;
    to_date: string;
  };
  download_url: string;
  inv_account_id: number;
  created_at: string;
  generate_at: string | null;
  key: string | null;
}

export enum DsFinvkReportStatus {
  InProgress = 'IN_PROGRESS',
  NoCpc = 'NO_CPC_FOUND',
  Failed = 'FAILED',
  Succeeded = 'SUCCEEDED',
  Initial = 'INITIAL',
}

export interface EmployeeReportTableResult {
  next: string | null;
  previous: string | null;
  results: EmployeeReport[];
}

export interface UserTransactionsResult {
  next: string | null;
  previous: string | null;
  results: EmployeeReportItem[];
}

export interface EmployeeReport {
  name: string;
  revenue_overall_total: string;
  invoices_total: number;
  service_revenue_total: string;
  service_count_total: number;
  retail_revenue_total: string;
  revenue_overall_avg: string;
  product_sold_total: number;
  retail_revenue_avg: string;
  service_revenue_avg: string;
  service_count_avg: string;
  product_sold_avg: string;
  user_id: number;
}

export interface EmployeeReportTableParams extends TableParams<EmployeeReport> {
  cursor: Cursor;
  sorter: SorterResult<EmployeeReport> | null;
  limit: number;
}

export interface EmployeeReportItem {
  product_name: string;
  product_type: string;
  discounts_applied: string | number;
  revenue: number;
  quantity: string;
}

export interface EmployeeRevenueDownloadParams {
  start_date: Dayjs;
  end_date: Dayjs;
  report_name: string;
  locale?: string;
}

export interface EmployeeTransactionTableParams
  extends TableParams<EmployeeReport> {
  cursor: Cursor;
}
